import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LTVStudioCreatorPage = () => {
  const navigate = useNavigate();
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Features included in the package
  const features = [
    { icon: '🎬', title: '6K Video Recording', description: 'Professional grade video quality' },
    { icon: '🎤', title: 'Professional Audio', description: 'Studio-quality sound recording' },
    { icon: '💡', title: 'Expert Lighting', description: 'Professional lighting setup' },
    { icon: '👨‍💻', title: 'Technical Support', description: 'On-site expert assistance' },
    { icon: '📺', title: 'LTV VOD Distribution', description: 'Your content goes live worldwide' },
    { icon: '💰', title: 'Revenue Share', description: 'Earn from your content views' },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="bg-black/90 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-white">LTV</span>
              <span className="text-2xl font-bold text-orange-500">STUDIO</span>
            </div>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="https://ltvproduction.com" className="text-gray-300 hover:text-white transition">VOD</a>
              <a href="https://ltvproduction.com/live" className="text-gray-300 hover:text-white transition">Live Events</a>
              <a href="#" className="text-white font-medium">Creator Package</a>
              <a href="https://ltvstudio.co.uk/about" className="text-gray-300 hover:text-white transition">About</a>
              <a href="https://ltvstudio.co.uk/contact" className="text-gray-300 hover:text-white transition">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Background gradient */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(180deg, rgba(139,92,46,0.3) 0%, rgba(0,0,0,1) 100%)',
          }}
        />
        
        {/* Animated lines overlay */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 100px, rgba(255,255,255,0.03) 100px, rgba(255,255,255,0.03) 101px)',
          }} />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-block mb-8">
            <span className="bg-gray-800 text-green-400 px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wider">
              For Content Creators
            </span>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-12">
            Record at London's #1 Studio
          </h1>
          
          {/* Pricing */}
          <div className="mb-8">
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-6xl sm:text-7xl font-bold text-orange-500">£50</span>
              <span className="text-2xl text-gray-400">/hour</span>
            </div>
            
            <div className="flex items-center justify-center gap-3 mt-4">
              <span className="text-xl text-gray-500 line-through">£200/hour</span>
              <span className="bg-green-500 text-white px-3 py-1 rounded text-sm font-bold">
                75% OFF
              </span>
            </div>
          </div>
          
          {/* Description */}
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Get the full LTV Studio experience – 6K video, professional audio, 
            expert support. Your content goes live on LTV VOD for viewers worldwide.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => window.open('https://ltvstudio.co.uk/booking', '_blank')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-4 rounded-lg transition-all transform hover:scale-105 text-lg"
            >
              Book Your Session
            </button>
            <button
              onClick={() => navigate('/creator')}
              className="bg-gray-700 hover:bg-gray-600 text-white font-bold px-8 py-4 rounded-lg transition-all text-lg"
            >
              View Packages
            </button>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">What's Included</h2>
          <p className="text-gray-400 mb-12">Everything you need for professional content creation</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 hover:border-orange-500/50 transition-all"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-gray-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">How It Works</h2>
          <p className="text-gray-400 text-center mb-12">Simple process, professional results</p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Book Online', desc: 'Choose your date and time' },
              { step: '2', title: 'Record', desc: 'Create your content at our studio' },
              { step: '3', title: 'Review', desc: 'Get your professional footage' },
              { step: '4', title: 'Go Live', desc: 'Publish on LTV VOD worldwide' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-2xl font-bold text-black mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Create?</h2>
          <p className="text-gray-400 mb-8 text-lg">
            Join hundreds of creators who've recorded at LTV Studio
          </p>
          <button
            onClick={() => window.open('https://ltvstudio.co.uk/booking', '_blank')}
            className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-10 py-4 rounded-lg transition-all transform hover:scale-105 text-xl"
          >
            Book Your Session Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-white">LTV</span>
              <span className="text-xl font-bold text-orange-500">STUDIO</span>
            </div>
            
            <div className="flex gap-6 text-gray-400">
              <a href="https://ltvproduction.com" className="hover:text-white transition">LTV VOD</a>
              <a href="https://ltvstudio.co.uk" className="hover:text-white transition">LTV Studio</a>
              <a href="https://ltvstudio.co.uk/contact" className="hover:text-white transition">Contact</a>
            </div>
            
            <p className="text-gray-500 text-sm">
              © 2026 LTV Studio. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LTVStudioCreatorPage;
